// @flow
import RadioGroup from "../components/formfields/RadioGroup";
import CheckboxGroup from "../components/formfields/CheckboxGroup";
import CustomField from "../components/formfields/CustomField";
import HiddenField from "../components/formfields/HiddenField";
import TextField from "../components/formfields/TextField";
import TextArea from "../components/formfields/TextArea";
import RTE from "../components/formfields/RTE";
import FileUploadField from "../components/formfields/FileUpload";
import GalleryImageField from "../components/formfields/GalleryImageField";

import RecaptchaField from "../components/formfields/RecaptchaField";
import * as React from "react";
import PubSub from "../utils/pubsub";
import moment from "moment";
import Config from "../constants/Config";
import _ from "lodash";
import { scrollTo } from "@utils/";

import { ReactForm, ReactFormField } from "@adrenalin/react-form";

// $FlowFixMe
//import { ReactForm, ReactFormField } from "../../../../../adrenalin/react-form/build"; // prettier-ignore

import useMergableState from "../hooks/useMergableState";
import { useEffect, useRef } from "react";

type PropsType = {
    formData: Object,
    steps: ?Array<Object>,
    formProcessor: Function,
    recaptchaSiteKey: string,
    submitText: ?string,
    cancelUrl: ?string,
    cancelText: ?string,
    hideGoogleRecaptcha: ?boolean,
};

const ReactFormBuilder = (props: PropsType): React.Node => {
    const [activeStep, setStep] = React.useState(0);
    const defaultValues = {};
    const fieldReferences = {};
    const reactFormReference: any = useRef(null);
    for (const section of props.formData) {
        for (const field of section.fields) {
            if (field.type === "gallery") {
                field.gallery.forEach((row: Object, idx: number): any => {
                    const captionName = `${field.name}_${idx + 1}_caption`;
                    const youtubeIdName = `${field.name}_${idx + 1}_youtubeId`;
                    const imageName = `${field.name}_${idx + 1}_image`;
                    const indexName = `${field.name}_${idx + 1}_orderIndex`;
                    const fileName = `${field.name}_${idx + 1}_file`;
                    const filefield = `${field.name}_${idx + 1}_filefield`;
                    const actionName = `${field.name}_${idx + 1}_action`;
                    defaultValues[captionName] = `${row.caption}`;
                    defaultValues[youtubeIdName] = `${row.youtube}`;
                    defaultValues[imageName] = `${row.image}`;
                    defaultValues[indexName] = `${idx + 1}`;
                    defaultValues[filefield] = fileName;
                    defaultValues[actionName] = `ignore`;
                });
                continue;
            }
            let value = field.value;
            if (field.type === "checkbox") {
                value = {};
                field.options.map((option: Object): any => {
                    value[option.value] = option.checked;
                    return null;
                });
            }
            defaultValues[field.name] = value;
        }
    }
    useEffect(() => {
        PubSub.subscribe(`datepicker`, (data: any) => {
            if (data.type === "dateSelection") {
                setInputValues({
                    [data.id]: data.date,
                });
                if (fieldReferences[data.id].current) {
                    fieldReferences[data.id].current.focus();
                }
            }
        });
    }, []);

    const [inputValues, setInputValues] = useMergableState(defaultValues);
    const customValidators = [];

    customValidators.push({
        ID: "notEmptyHtml",
        validate: (input: Object): boolean => {
            return (
                !!input.value && input.value.replace(/<[^>]*>/g, "").length > 0
            );
        },
        error: "Please enter a value for this field.",
    });

    const isLastStep =
        !props.steps ||
        props.steps.length === 0 ||
        activeStep === props.steps.length - 1;

    const addCustomValidator = (validatorsList: Object, fieldName: string) => {
        for (const validationObject of validatorsList) {
            const validatorId = `${fieldName}_${validationObject.type}`;

            if (validationObject.type === "minCharLength") {
                customValidators.push({
                    ID: validatorId,
                    validate: (input: Object): boolean => {
                        return input.value.length >= validationObject.value;
                    },
                    error: validationObject.errorMessage,
                });
            } else if (validationObject.type === "maxCharLength") {
                customValidators.push({
                    ID: validatorId,
                    validate: (input: Object): boolean => {
                        return input.value.length <= validationObject.value;
                    },
                    error: validationObject.errorMessage,
                });
            } else if (validationObject.type === "regularExpression") {
                const regex = new RegExp(validationObject.value);
                customValidators.push({
                    ID: validatorId,
                    validate: (input: Object): boolean => {
                        return input.value.match(regex);
                    },
                    error: validationObject.errorMessage,
                });
            } else if (validationObject.type === "fileSize") {
                customValidators.push({
                    ID: validatorId,
                    validate: (input: Object): boolean => {
                        return (
                            input.value &&
                            input.value.size <= validationObject.value
                        );
                    },
                    error: validationObject.errorMessage,
                });
            } else if (validationObject.type === "fileType") {
                customValidators.push({
                    ID: validatorId,
                    validate: (input: Object): boolean => {
                        const extension = input.value.name
                            .split(".")
                            .pop()
                            .toLowerCase();

                        //return false;
                        return validationObject.value.includes(extension);
                    },
                    error: validationObject.errorMessage,
                });
            } else if (validationObject.type === "validDate") {
                customValidators.push({
                    ID: validatorId,
                    validate: (input: Object): boolean => {
                        const date = moment(input.value, Config.dateFormat);
                        return date.isValid();
                    },
                    error: "Invalid date format",
                });
            } else if (validationObject.type === "beforeDate") {
                //This validator doesn't work because the validation function can't access state from other components
                customValidators.push({
                    ID: validatorId,
                    validate: (input: Object): boolean => {
                        const date = moment(input.value, Config.dateFormat);
                        const dateBefore = moment(
                            fieldReferences[validationObject.value].current
                                .value,
                            Config.dateFormat
                        );
                        return (
                            date.isValid() &&
                            dateBefore.isValid() &&
                            (dateBefore.isBefore(date) ||
                                dateBefore.isSame(date))
                        );
                    },
                    error: "Date must be after start date",
                });
            }
        }
    };
    const mapValidators = (validatorsList: Object, fieldName: string): any => {
        const validatorKeys = [];
        for (const validationObject of validatorsList) {
            const validatorId = `${fieldName}_${validationObject.type}`;
            validatorKeys.push(validatorId);
            validatorKeys.push(validationObject.type);
        }
        return validatorKeys;
    };
    const mapPropsToReactField = (input: any): any => {
        const typeMapping = {
            text: "text",
            tel: "tel",
            email: "email",
            textarea: "textarea",
            select: "select",
            file: "file",
            date: "date",
            label: "label",
        };

        const type = typeMapping[input.type];

        const mappedFields: any = {
            type,
            label: input.label,
            name: input.name,
            placeholder: input.placeholder || "",
            required: input.required || false,
            title: input.tooltip || "",
            info: input.info,
            validators: mapValidators(input.validations, input.name),
        };
        addCustomValidator(input.validations, input.name);

        if (type !== "file") {
            mappedFields.value = input.value || "";
        }

        // Add validators
        if (input.type === "tel") {
            mappedFields.validators.push("mobile");
        }
        if (input.type === "email") {
            mappedFields.validators.push("email");
        }

        _.each(input.validations, (validation: Object) => {
            if (validation.type === "required") {
                mappedFields.errorMessages = {
                    required: validation.errorMessage,
                };
                mappedFields.required = true;
            }
        });

        if (input.type === "date") {
            addCustomValidator(
                [
                    {
                        type: "validDate",
                    },
                ],
                input.name
            );
            mappedFields.validators.push(`${input.name}_validDate`);
        }
        if (input.type === "file") {
            addCustomValidator(
                [
                    {
                        type: "fileType",
                        value: [
                            "jpg",
                            "jpeg",
                            "png",
                            "pdf",
                            "txt",
                            "doc",
                            "docx",
                            "xls",
                            "xlsx",
                            "zip",
                            "csv",
                        ],
                        errorMessage:
                            "Invalid File Type or size for images. Only images with type: jpg or png and maximum size: 4mb can be uploaded.",
                    },
                    {
                        type: "fileSize",
                        value: 4194304,
                        errorMessage:
                            "Invalid File Type or size for images. Only images with type: jpg or png and maximum size: 4mb can be uploaded.",
                    },
                ],
                input.name
            );
            mappedFields.validators.push(`${input.name}_fileSize`);
            mappedFields.validators.push(`${input.name}_fileType`);
        }

        return mappedFields;
    };

    const autoFormatDate = (event: any, type: string, name: string) => {
        if (type === "date") {
            const date = moment(event.target.value, Config.dateFormat);
            if (date.isValid()) {
                setInputValues({
                    [name]: date.format(Config.dateFormat),
                });
            }
        }
    };

    const autoFormatPrice = (event: any, type: string, name: string) => {
        if (type === "price") {
            const roundedPrice = Math.round(event.target.value * 100) / 100;
            setInputValues({
                [name]: roundedPrice,
            });
        }
    };

    const renderElement = (
        element: any,
        index: number,
        isActive: boolean = true
    ): React.Node => {
        switch (element.type) {
            case "text":
            case "number":
            case "email":
            case "tel":
            case "date":
                fieldReferences[element.name] = useRef(null);
                let disabled = false;
                if (
                    element.name === "SuppliedPrice" &&
                    inputValues["Free"] &&
                    inputValues["Free"]["Free"]
                ) {
                    disabled = true;
                }
                return (
                    <ReactFormField
                        {...mapPropsToReactField(element)}
                        className={
                            element.type === "date" ? "has-datepicker" : ""
                        }
                        required={
                            disabled
                                ? false
                                : isActive &&
                                  mapPropsToReactField(element).required
                        }
                        value={inputValues[element.name]}
                        type={element.type === "date" ? "text" : element.type}
                        fieldComponent={TextField}
                        ref={fieldReferences[element.name]}
                        onChange={(event: any) => {
                            setInputValues({
                                [element.name]: event.target.value,
                            });
                        }}
                        onBlur={(event: any) => {
                            autoFormatDate(event, element.type, element.name);
                        }}
                        disabled={disabled}
                    ></ReactFormField>
                );
            case "textarea":
                const customProps = {};
                _.each(element.validations, (validation: Object) => {
                    if (validation.type === "maxCharLength") {
                        customProps["className"] = `max-${validation.value}`;
                    }
                });

                return (
                    <ReactFormField
                        {...mapPropsToReactField(element)}
                        {...customProps}
                        value={inputValues[element.name]}
                        required={
                            isActive && mapPropsToReactField(element).required
                        }
                        type={element.type}
                        fieldComponent={TextArea}
                        onChange={(event: any) => {
                            setInputValues({
                                [element.name]: event.target.value,
                            });
                        }}
                    ></ReactFormField>
                );
            case "radio":
                return (
                    <RadioGroup
                        label={element.label}
                        info={element.info || ""}
                        tooltip={element.tooltip || ""}
                    >
                        <div className="radio-group-col">
                            {element.options.map(
                                (option: any, index: number): React.Node => (
                                    <ReactFormField
                                        key={index}
                                        type={"radio"}
                                        name={option.name}
                                        label={option.label}
                                        value={option.value}
                                        info={""}
                                        fieldComponent={CustomField}
                                        checked={
                                            inputValues[option.name] ===
                                            option.value
                                        }
                                        onChange={(event: any) => {
                                            setInputValues({
                                                [option.name]: option.value,
                                            });
                                        }}
                                        required={element.required}
                                    ></ReactFormField>
                                )
                            )}
                        </div>
                    </RadioGroup>
                );
            case "checkbox":
                return (
                    <CheckboxGroup
                        label={element.label}
                        info={element.info}
                        tooltip={element.tooltip || ""}
                    >
                        <div className="radio-group-col">
                            {element.options.map(
                                (option: any, index: number): React.Node => (
                                    <ReactFormField
                                        {...mapPropsToReactField(element)}
                                        key={index}
                                        type={"checkbox"}
                                        fieldComponent={CustomField}
                                        name={option.value}
                                        info={""}
                                        value={
                                            inputValues[option.name][
                                                option.value
                                            ]
                                                ? "on"
                                                : ""
                                        }
                                        label={option.label}
                                        id={option.value}
                                        checked={
                                            inputValues[option.name][
                                                option.value
                                            ]
                                        }
                                        onChange={(event: any) => {
                                            const newValue =
                                                inputValues[option.name];
                                            newValue[option.value] =
                                                event.target.checked;
                                            setInputValues({
                                                [option.name]: newValue,
                                            });
                                        }}
                                    ></ReactFormField>
                                )
                            )}
                        </div>
                    </CheckboxGroup>
                );
            case "select":
                return (
                    <ReactFormField
                        {...mapPropsToReactField(element)}
                        value={inputValues[element.name] || ""}
                        className={
                            element.placeholder && !inputValues[element.name]
                                ? "has-placeholder"
                                : ""
                        }
                        onChange={(event: any) => {
                            setInputValues({
                                [element.name]: event.target.value,
                            });
                        }}
                    >
                        {element.placeholder && !inputValues[element.name] && (
                            <option value="">{element.placeholder}</option>
                        )}
                        {element.options.map(
                            (option: any, index: number): React.Node => (
                                <option value={option.value} key={index}>
                                    {option.label}
                                </option>
                            )
                        )}
                    </ReactFormField>
                );
            case "file":
                return (
                    <ReactFormField
                        {...mapPropsToReactField(element)}
                        fieldComponent={FileUploadField}
                        type={element.type}
                    ></ReactFormField>
                );
            case "label":
                return (
                    <>
                        <p className={"sub-heading__title"}>{element.title}</p>
                        <p className={"sub-heading__description"}>
                            {element.description}
                        </p>
                    </>
                );
            case "heading":
                return (
                    <>
                        <p className={"heading__title"}>{element.label}</p>
                    </>
                );
            case "gallery":
                return (
                    <>
                        {element.gallery.map(
                            (row: Object, idx: number): React.Node => {
                                const filefield = `${element.name}_${idx +
                                    1}_filefield`;
                                const fileName = `${element.name}_${idx +
                                    1}_file`;
                                const captionName = `${element.name}_${idx +
                                    1}_caption`;
                                const youtubeIdName = `${element.name}_${idx +
                                    1}_youtubeId`;
                                const indexName = `${element.name}_${idx +
                                    1}_orderIndex`;
                                const actionName = `${element.name}_${idx +
                                    1}_action`;
                                const imageName = `${element.name}_${idx +
                                    1}_image`;
                                return (
                                    <div
                                        className="react-form__gallery"
                                        key={element.name + idx}
                                    >
                                        <ReactFormField
                                            name={fileName}
                                            type={"file"}
                                            accept="image/*"
                                            fieldComponent={GalleryImageField}
                                            required={
                                                isActive &&
                                                !inputValues[imageName] &&
                                                mapPropsToReactField(element)
                                                    .required
                                            }
                                            errorMessages={{
                                                required:
                                                    "Please upload an image.",
                                            }}
                                            defaultImage={
                                                inputValues[imageName]
                                            }
                                            onChange={(event: any) => {
                                                if (
                                                    event.target.files.length >
                                                    0
                                                ) {
                                                    setInputValues({
                                                        [fileName]:
                                                            event.target
                                                                .files[0],
                                                        [actionName]: "upload",
                                                        [imageName]: "",
                                                    });
                                                } else {
                                                    setInputValues({
                                                        [fileName]: null,
                                                        [actionName]: "delete",
                                                        [imageName]: "",
                                                    });
                                                }
                                            }}
                                        ></ReactFormField>
                                        <div>
                                            <ReactFormField
                                                name={captionName}
                                                value={inputValues[captionName]}
                                                type={"text"}
                                                label="Caption (optional)"
                                                fieldComponent={TextField}
                                                onChange={(event: any) => {
                                                    setInputValues({
                                                        [captionName]:
                                                            event.target.value,
                                                    });
                                                }}
                                                onBlur={(event: any) => {
                                                    autoFormatDate(
                                                        event,
                                                        "text",
                                                        captionName
                                                    );
                                                }}
                                            ></ReactFormField>
                                            <ReactFormField
                                                name={youtubeIdName}
                                                value={
                                                    inputValues[youtubeIdName]
                                                }
                                                type={"text"}
                                                fieldComponent={TextField}
                                                label="Youtube ID (optional)"
                                                onChange={(event: any) => {
                                                    setInputValues({
                                                        [youtubeIdName]:
                                                            event.target.value,
                                                    });
                                                }}
                                                onBlur={(event: any) => {
                                                    autoFormatDate(
                                                        event,
                                                        "text",
                                                        youtubeIdName
                                                    );
                                                }}
                                            ></ReactFormField>
                                        </div>
                                        <div>
                                            <ReactFormField
                                                name={indexName}
                                                value={inputValues[indexName]}
                                                type={"text"}
                                                fieldComponent={TextField}
                                                label="Order"
                                                onChange={(event: any) => {
                                                    setInputValues({
                                                        [indexName]:
                                                            event.target.value,
                                                    });
                                                }}
                                                onBlur={(event: any) => {
                                                    autoFormatDate(
                                                        event,
                                                        "text",
                                                        indexName
                                                    );
                                                }}
                                            ></ReactFormField>
                                        </div>
                                        <div>
                                            <ReactFormField
                                                value={
                                                    inputValues[filefield] || ""
                                                }
                                                type="text"
                                                fieldComponent={HiddenField}
                                                required={true}
                                                name={filefield}
                                            ></ReactFormField>
                                            <ReactFormField
                                                value={
                                                    inputValues[actionName] ||
                                                    ""
                                                }
                                                type="text"
                                                fieldComponent={HiddenField}
                                                required={true}
                                                name={actionName}
                                            ></ReactFormField>
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </>
                );
            case "rte":
                const rteCustomProps = {};
                _.each(element.validations, (validation: Object) => {
                    if (validation.type === "maxCharLength") {
                        rteCustomProps["className"] = `max-${validation.value}`;
                    }
                });
                const validators = mapPropsToReactField(element).validators;
                if (isActive && mapPropsToReactField(element).required) {
                    validators.push("notEmptyHtml");
                }
                return (
                    <ReactFormField
                        {...mapPropsToReactField(element)}
                        {...rteCustomProps}
                        value={inputValues[element.name]}
                        required={
                            isActive && mapPropsToReactField(element).required
                        }
                        validators={validators}
                        type={"textarea"}
                        fieldComponent={RTE}
                        onChange={(event: any) => {
                            setInputValues({
                                [element.name]: event.target.value,
                            });
                        }}
                    ></ReactFormField>
                );
            case "recaptcha":
                return <div></div>;
            case "price":
                fieldReferences[element.name] = useRef(null);
                return (
                    <ReactFormField
                        {...mapPropsToReactField(element)}
                        className="is-price"
                        required={
                            isActive && mapPropsToReactField(element).required
                        }
                        value={inputValues[element.name]}
                        type="number"
                        min={0}
                        fieldComponent={TextField}
                        ref={fieldReferences[element.name]}
                        onChange={(event: any) => {
                            setInputValues({
                                [element.name]: event.target.value,
                            });
                        }}
                        onBlur={(event: any) => {
                            autoFormatPrice(event, element.type, element.name);
                        }}
                    ></ReactFormField>
                );
            default:
                return <p>Unkown form element type: {element.type}</p>;
        }
    };

    const renderSection = (
        section: any,
        sectionIndex: number,
        isActive: boolean = true
    ): React.Node => (
        <fieldset className="react-form__section" key={sectionIndex}>
            {section.title && (
                <legend className="react-form__section-label">
                    {section.title}
                </legend>
            )}

            <div className="react-form__section__container">
                {section.fields.map(
                    (element: any, index: number): React.Node => (
                        <div
                            className={`react-form__element react-form__element--${
                                element.type
                            } ${
                                element.halfSize
                                    ? "react-form__element--half"
                                    : ""
                            } ${
                                element.subHeading
                                    ? "react-form__element--has-subheading"
                                    : ""
                            }`}
                            key={index}
                            data-sub-heading={element.subHeading}
                        >
                            {renderElement(element, index, isActive)}
                            {element.type === "date" ? (
                                <button
                                    type="button"
                                    className="react-form__datepicker-icon"
                                    onClick={(event: any) => {
                                        event.preventDefault();
                                        PubSub.publish("datepicker", {
                                            type: "openLightbox",
                                            id: element.name,
                                        });
                                    }}
                                    aria-label="Open Datepicker"
                                >
                                    <i className="icon icon-calendar" />
                                </button>
                            ) : null}
                        </div>
                    )
                )}
            </div>
        </fieldset>
    );
    return (
        <ReactForm
            validators={customValidators}
            onSuccess={(
                data: Object,
                state: any,
                e: any,
                formDataObject: any
            ) => {
                if (
                    props.steps &&
                    props.steps.length > 0 &&
                    activeStep < props.steps.length - 1
                ) {
                    reactFormReference.current.resetErrors();
                    setStep(activeStep + 1);
                    scrollTo(
                        reactFormReference.current.formRef.current,
                        0,
                        500
                    );
                    return;
                }
                props.formProcessor(formDataObject);
            }}
            className="react-form__form"
            ref={reactFormReference}
        >
            {props.steps && props.steps.length && (
                <div className="step-indicators-container">
                    {props.steps.map((step: any, index: number): React.Node => {
                        return (
                            <div
                                key={index}
                                className={`step-indicator ${
                                    index <= activeStep ? "hightlight" : ""
                                }`}
                            >
                                <div className={`step-indicator-icon`}>
                                    {index < activeStep ? (
                                        <i className={`icon icon-tick`}></i>
                                    ) : (
                                        index + 1
                                    )}
                                </div>
                                <div className={`step-indicator-text`}>
                                    {step.Title}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {props.steps && props.steps.length
                ? props.steps.map(
                      (step: any, stepIndex: number): React.Node => (
                          <fieldset
                              className={`react-form__step ${
                                  stepIndex === activeStep ? "actived" : ""
                              }`}
                              key={stepIndex}
                          >
                              {step.Title && (
                                  <legend className="react-form__step-label">
                                      {step.Title}
                                  </legend>
                              )}
                              <div className="react-form__step__container">
                                  {step.Sections.map(
                                      (
                                          sectionIndex: number,
                                          index: number
                                      ): React.Node =>
                                          renderSection(
                                              props.formData[sectionIndex],
                                              index,
                                              stepIndex <= activeStep
                                          )
                                  )}
                              </div>
                          </fieldset>
                      )
                  )
                : props.formData.map(renderSection)}
            {/* Render main inputs of form */}

            <ReactFormField
                value={inputValues["GoogleRecaptchaResponse"] || ""}
                type="text"
                fieldComponent={RecaptchaField}
                required={!props.hideGoogleRecaptcha && isLastStep}
                recaptchaSiteKey={props.recaptchaSiteKey}
                name="GoogleRecaptchaResponse"
                ref={fieldReferences["foobar"]}
                onChange={(event: any) => {
                    setInputValues({
                        [event.target.name]: event.target.value,
                    });
                }}
            ></ReactFormField>

            {props.cancelUrl && isLastStep ? (
                <button
                    type="button"
                    className="react-form__button cta cta--inline cta--big cta--secondary"
                    onClick={() => {
                        window.open(props.cancelUrl, "_self");
                    }}
                >
                    {props.cancelText || "Cancel"}
                </button>
            ) : (
                props.steps &&
                props.steps.length > 0 && (
                    <button
                        type="button"
                        className="react-form__button cta cta--inline cta--big cta--secondary"
                        onClick={() => {
                            if (activeStep === 0) {
                                window.history.back();
                            } else {
                                reactFormReference.current.resetErrors();
                                setStep(activeStep - 1);
                                scrollTo(
                                    reactFormReference.current.formRef.current,
                                    0,
                                    500
                                );
                            }
                        }}
                    >
                        {"Previous"}
                    </button>
                )
            )}
            <button
                type="submit"
                className="react-form__button cta cta--inline cta--big"
            >
                {props.steps &&
                props.steps.length > 0 &&
                activeStep < props.steps.length - 1
                    ? "Next"
                    : props.submitText || "Submit"}
            </button>
        </ReactForm>
    );
};

export default ReactFormBuilder;
